// src/components/SettingsDropdown.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAccess } from '../contexts/AccessContext';
import { SETTINGS_NAV, SETTINGS_LEAVES, isEntryVisible } from '../config/settingsNav';
import type { SettingsTab } from '../config/settingsNav';

export type { SettingsTab };

interface SettingsDropdownProps {
  onSelectSetting?: (tab: SettingsTab) => void;
  onLogout?: () => void;
}

export function SettingsDropdown({ onSelectSetting, onLogout }: SettingsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();
  const { hasAccess, isPathVisible } = useAccess();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsOpen(false);
    if (onLogout) {
      onLogout();
    } else {
      logout();
    }
  };

  const navigate = useNavigate();
  const role = user?.role || "admin";

  // Top-level menu = grouped nav (3 groups + 2 standalone items). A group shows
  // when at least one of its children is visible; a standalone item shows by its
  // own rule. Access profile is authoritative when present, else fall back to
  // role — same gate as before, just lifted to the group level.
  const visibleEntries = SETTINGS_NAV.filter((entry) =>
    isEntryVisible(entry, { hasAccess, isPathVisible, role })
  );
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Settings Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
//         onClick={() => {
//           navigate(`/settings/${setting.id}`);
//           setIsOpen(false);
// }}
        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors relative group"
        title="Settings"
      >
        <Settings size={22} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="fixed top-24 right-2 left-2 w-auto md:absolute md:top-full md:right-0 md:left-auto md:mt-2 md:w-72 md:max-w-[calc(100vw-1rem)] bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          {/* User Info Header — click to open the profile page */}
          <button
            onClick={() => { navigate('/profile'); setIsOpen(false); }}
            className="w-full text-left p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-lg">
                {user?.profile_photo_url
                  ? <img src={user.profile_photo_url} alt="" className="w-full h-full object-cover" />
                  : (user?.name?.charAt(0) || user?.email?.charAt(0) || 'U')}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">{user?.name || 'User'}</p>
                <p className="text-xs text-gray-500">{user?.email || 'user@example.com'}</p>
              </div>
            </div>
          </button>

          
          <div className="py-1 max-h-96 overflow-y-auto">
            {visibleEntries.map((entry) => {
              const isGroup = entry.kind === 'group';
              const leaf = isGroup ? null : SETTINGS_LEAVES[entry.id];
              const label = isGroup ? entry.label : leaf!.label;
              const Icon = isGroup ? entry.icon : leaf!.icon;
              return (
                <button
                  key={entry.id}
                  onClick={() => {
                    navigate(`/settings/${entry.id}`);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-gray-50 transition-colors text-left group text-gray-700"
                >
                  <div className="p-1 rounded-md transition-colors text-gray-500 group-hover:text-blue-600">
                    <Icon size={16} />
                  </div>
                  <span className="text-sm font-medium flex-1">{label}</span>
                  <ChevronRight
                    size={13}
                    className={`text-gray-400 transition-opacity ${isGroup ? 'opacity-60' : 'opacity-0 group-hover:opacity-100'}`}
                  />
                </button>
              );
            })}
          </div>

          {/* Logout Button — matches the new compact row height. */}
          <div className="border-t border-gray-100 py-1">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-red-50 transition-colors text-left text-red-600 group"
            >
              <div className="p-1 rounded-md text-red-500 group-hover:text-red-600">
                <LogOut size={16} />
              </div>
              <span className="text-sm font-medium flex-1">Logout</span>
              <ChevronRight size={13} className="text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideInFromTop {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-in {
          animation-duration: 0.2s;
          animation-fill-mode: both;
        }
        
        .fade-in {
          animation-name: fadeIn;
        }
        
        .slide-in-from-top-2 {
          animation-name: slideInFromTop;
        }
      `}</style>
    </div>
  );
}